import {
  AuthorizeView,
  Button,
  Label,
  Loading,
  TextInput,
  Unform,
  AddObservation,
  AutocompleteInput,
  TextAreaInput,
  RadioInput,
  SelectInput,
} from 'components';
import { BodyProps } from 'constants/MultipleAcorddions';
import React, { memo, useCallback, useContext, useEffect } from 'react';
import { Flex, Box, Stack } from '@chakra-ui/react';
import { WarningMessage } from 'components/WarningMessage';
import { SubmitHandler } from '@unform/core';
import { MessageInfo } from 'components/MessageInfo';
import { checkPermission } from 'utils/authentication';
import { getAllClients } from 'services/client';
import Evaluation from 'models/Forms/Evaluation';

import { evaluationCtx } from '../../index';
import { getLoggedUser } from 'utils/storage';

interface IProps extends BodyProps {}

interface ItemProps {
  heading: String;
  text: String;
}

type ClientProps = {
  name: string;
  id: React.ReactText;
};

const text1 =
  'qualquer ato que envolva dados pessoais, como visualização, coleta, armazenamento, compartilhamento, exclusão, etc.';
const text2 = 'qualquer informação direta ou indiretamente relacionada a uma pessoa física.';
const text3 =
  'nomes, números de documentos identificadores, endereços, fotografias, telefone, dados bancários, dados de saúde, histórico de consumo, IPs de acesso, e-mail, estado civil, situação fiscal, histórico de navegação, cookies, etc.';

const PersonalDataComponent: React.FC<IProps> = ({
  index,
  functionCallback,
  submit,
  completed,
}) => {
  const [clients, setClients] = React.useState<{ name: string; id: React.ReactText }[]>([]);

  const ctx = useContext(evaluationCtx);
  const { load, setIsLoad } = useContext(evaluationCtx);

  const [loading, setLoading] = React.useState<boolean>(false);

  const MessageInfoItems: ItemProps[] = [
    {
      heading: 'Tratamento: ',
      text: text1,
    },
    {
      heading: 'Dados pessoais: ',
      text: text2,
    },
    {
      heading: 'Exemplos (não se limitando a): ',
      text: text3,
    },
  ];

  const handleSubmit = useCallback<SubmitHandler<Evaluation>>(
    async data => {
      setLoading(true);
      if (submit) {
        const resp = await submit(data, '1 - Dados Pessoais');
        setIsLoad(!load);
        if (resp) {
          functionCallback(index + 1);
        }
      } else {
        functionCallback(index + 1);
      }
      setLoading(false);
    },
    [functionCallback, submit],
  );

  useEffect(() => {
    if (checkPermission({ onlyAdmin: true })) {
      (async () => {
        const { data } = await getAllClients('');
        setClients(data);
        localStorage.setItem('clients', JSON.stringify(data));
      })();
    }
  }, []);

  const clientSelect = clients.find(i => +i.id === +(getLoggedUser().client || '')) as ClientProps;

  return (
    <Flex marginTop="1rem" width="100%" direction="column">
      <WarningMessage
        mb="29px"
        text={
          'O objetivo deste formulário é realizar uma avaliação mais rápida e precisa sobre uma atividade de tratamento de dados. Inserir informações falsas ou inexatas apenas dificultará a análise e prestação de serviços jurídicos, além de tirar a utilidade do registro.'
        }
      />
      <Unform
        initialData={ctx.data}
        onSubmit={handleSubmit}
        // validationSchema={personalDataValidationSchema}
      >
        {!ctx.isDetails && (
          <AuthorizeView onlyAdmin>
            <Box mb="20px">
              {clients.length > 0 ? (
                !getLoggedUser()?.client ? (
                  <AutocompleteInput
                    name="clientId"
                    optionLabel="name"
                    optionValue="id"
                    label="Cliente"
                    options={clients}
                  />
                ) : (
                  <SelectInput
                    name="clientId"
                    optionLabel="name"
                    optionValue="id"
                    label="Cliente"
                    options={
                      clients
                        ?.map(client => ({ name: client.name, id: client.id }))
                        .sort((a, b) => {
                          return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
                        }) || []
                    }
                    defaultValues={[clientSelect]}
                    // {...cProps}
                    disabled
                  />
                )
              ) : (
                <Loading />
              )}
            </Box>
          </AuthorizeView>
        )}
        {ctx.isDetails && (
          <AuthorizeView onlyAdmin>
            <Box mb="20px">
              {clients.length > 0 ? (
                <SelectInput
                  name="clientId"
                  optionLabel="name"
                  optionValue="id"
                  label="Cliente"
                  options={
                    clients
                      ?.map(client => ({ name: client.name, id: client.id }))
                      .sort((a, b) => {
                        return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
                      }) || []
                  }
                  defaultValues={[clientSelect]}
                  // {...cProps}
                  disabled
                />
              ) : (
                <Loading />
              )}
            </Box>
          </AuthorizeView>
        )}
        {checkPermission({ onlyAdmin: true }) ? (
          <>
            <Box>
              <TextInput
                mt="8px"
                name="name"
                label="Identificação do projeto"
                // {...commonProps}
              />
            </Box>
            <Box mt="13px" disabled={!!ctx.data.name}>
              <TextAreaInput
                rows={3}
                resize="none"
                mt="8px"
                name="projectDescribe"
                label="Descreva em linhas gerais o projeto"
                // {...commonProps}
              />
            </Box>
            <Box mt="28px">
              <TextAreaInput
                rows={3}
                resize="none"
                mt="8px"
                name="responsable"
                label="Responsável pelo projeto e contato"
                // {...commonProps}
              />
            </Box>
            <Box mt="28px">
              <Label htmlFor="O projeto envolve tratamento de dados pessoais?">
                O projeto envolve tratamento de dados pessoais?
              </Label>
              <Box mt="8px">
                <RadioInput
                  label="Sim"
                  name="processingOfPersonalData"
                  value={ctx.data.processingOfPersonalData}
                />
                <AddObservation
                  isDetail={ctx.isDetails}
                  placeholder={'Observações sobre o tratamento de dados (opcional)'}
                  obsValue={ctx.data.jsonCustomObsObj.processingOfPersonalData}
                  onChangeEvent={e =>
                    ctx.handlePayload(prevState => ({
                      ...prevState,
                      jsonCustomObsObj: {
                        ...prevState.jsonCustomObsObj,
                        processingOfPersonalData: e.target.value,
                      },
                    }))
                  }
                />
              </Box>
            </Box>

            <WarningMessage
              mt="20px"
              text={
                'Lembre-se que o conceito de dado pessoal é extremamente amplo e vai além do nome ou número de documento de uma pessoa (ver quadro cinza abaixo). Na dúvida se o projeto envolve ou não dados pessoais, assinale SIM e continue a preencher o questionário.'
              }
            />

            <MessageInfo items={MessageInfoItems} marginTop="32px" marginBottom="20px" />
            {!ctx.isDetails && (
              <Box width="100%">
                <Stack direction={['column', 'row']} spacing="1rem" mt="1rem" justify="flex-end">
                  <Button variant="primary" type="submit" isLoading={loading} disabled={loading}>
                    Próximo
                  </Button>
                </Stack>
              </Box>
            )}
            {ctx.isDetails && (
              <Box width="100%">
                <Stack direction={['column', 'row']} spacing="1rem" mt="1rem" justify="flex-end">
                  <Button variant="primary" type="submit" isLoading={loading} disabled={loading}>
                    Próximo
                  </Button>
                </Stack>
              </Box>
            )}
          </>
        ) : (
          <>
            <Box>
              <TextInput
                mt="8px"
                name="name"
                label="Identificação do projeto"
                isDisabled={completed}
              />
            </Box>
            <Box mt="13px" disabled={!!ctx.data.name}>
              <TextAreaInput
                mt="8px"
                name="projectDescribe"
                label="Descreva em linhas gerais o projeto"
                isDisabled={completed}
              />
            </Box>
            <Box mt="28px">
              <TextAreaInput
                mt="8px"
                name="responsable"
                label="Responsável pelo projeto e contato"
                isDisabled={completed}
              />
            </Box>
            <Box mt="28px">
              <Label htmlFor="O projeto envolve tratamento de dados pessoais?">
                O projeto envolve tratamento de dados pessoais?
              </Label>
              <Box mt="8px">
                <RadioInput
                  label="Sim"
                  name="processingOfPersonalData"
                  value={ctx.data.processingOfPersonalData}
                  isDisabled={completed}
                />
                <AddObservation
                  isDetail={completed}
                  placeholder={'Observações sobre o tratamento de dados (opcional)'}
                  obsValue={ctx.data.jsonCustomObsObj.processingOfPersonalData}
                  onChangeEvent={e =>
                    ctx.handlePayload(prevState => ({
                      ...prevState,
                      jsonCustomObsObj: {
                        ...prevState.jsonCustomObsObj,
                        processingOfPersonalData: e.target.value,
                      },
                    }))
                  }
                />
              </Box>
            </Box>

            <WarningMessage
              mt="20px"
              text={
                'Lembre-se que o conceito de dado pessoal é extremamente amplo e vai além do nome ou número de documento de uma pessoa (ver quadro cinza abaixo). Na dúvida se o projeto envolve ou não dados pessoais, assinale SIM e continue a preencher o questionário.'
              }
            />

            <MessageInfo items={MessageInfoItems} marginTop="32px" marginBottom="20px" />
            {!completed && (
              <Box width="100%">
                <Stack direction={['column', 'row']} spacing="1rem" mt="1rem" justify="flex-end">
                  <Button variant="primary" type="submit" isLoading={loading} disabled={loading}>
                    Próximo
                  </Button>
                </Stack>
              </Box>
            )}
          </>
        )}
      </Unform>
    </Flex>
  );
};

export const PersonalData = memo(PersonalDataComponent);
